import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { adminAPI } from '../../services/api';
import './AdminPages.css';
import './Dashboard.css';

const CHART_COLORS = ['#E31E24', '#c41a1f', '#a01519', '#7a1012'];

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  const s = stats || {};
  const modelsCount = s.models_count || 0;
  const variantsCount = s.variants_count || 0;
  const spareCount = s.spare_parts_count || 0;
  const accessoriesCount = s.accessories_count || 0;
  const activeModels = s.active_models || 0;
  const activeVariants = s.active_variants || 0;
  const activeSpare = s.active_spare_parts || 0;
  const activeAccessories = s.active_accessories || 0;

  const totalItems = modelsCount + variantsCount + spareCount + accessoriesCount;

  const distributionData = [
    { name: 'الموديلات', value: modelsCount, count: modelsCount },
    { name: 'السيارات', value: variantsCount, count: variantsCount },
    { name: 'قطع الغيار', value: spareCount, count: spareCount },
    { name: 'كماليات', value: accessoriesCount, count: accessoriesCount },
  ].filter((d) => d.value > 0);

  const barData = [
    { name: 'الموديلات', total: modelsCount, active: activeModels },
    { name: 'السيارات', total: variantsCount, active: activeVariants },
    { name: 'قطع الغيار', total: spareCount, active: activeSpare },
    { name: 'كماليات', total: accessoriesCount, active: activeAccessories },
  ];

  const activeVsInactive = [
    { name: 'الموديلات', active: activeModels, inactive: modelsCount - activeModels },
    { name: 'السيارات', active: activeVariants, inactive: variantsCount - activeVariants },
    { name: 'قطع الغيار', active: activeSpare, inactive: spareCount - activeSpare },
    { name: 'كماليات', active: activeAccessories, inactive: accessoriesCount - activeAccessories },
  ];

  const renderCustomLabel = ({ name, percent }) => {
    if (percent < 0.05) return null;
    return `${name} ${(percent * 100).toFixed(0)}%`;
  };

  return (
    <div className="admin-dashboard">
      <div className="content-header">
        <h1>لوحة التحكم</h1>
        <p>نظرة عامة على المتجر والإحصائيات</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3 className="stat-label">إجمالي الموديلات</h3>
          <p className="stat-value">{modelsCount}</p>
          <p className="stat-sub">نشط: {activeModels}</p>
        </div>
        <div className="stat-card">
          <h3 className="stat-label">إجمالي السيارات</h3>
          <p className="stat-value">{variantsCount}</p>
          <p className="stat-sub">نشط: {activeVariants}</p>
        </div>
        <div className="stat-card">
          <h3 className="stat-label">إجمالي قطع الغيار</h3>
          <p className="stat-value">{spareCount}</p>
          <p className="stat-sub">نشط: {activeSpare}</p>
        </div>
        <div className="stat-card">
          <h3 className="stat-label">إجمالي الكماليات</h3>
          <p className="stat-value">{accessoriesCount}</p>
          <p className="stat-sub">نشط: {activeAccessories}</p>
        </div>
      </div>

      {totalItems > 0 && (
        <>
          <div className="dashboard-charts-section">
            <h2 className="charts-section-title">الرسوم البيانية</h2>
            <div className="dashboard-charts-grid">
              <div className="dashboard-chart-card">
                <h3 className="chart-card-title">توزيع المحتوى</h3>
                <p className="chart-card-desc">نسبة كل فئة من إجمالي العناصر ({totalItems} عنصر)</p>
                <div className="chart-container chart-container--pie">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={distributionData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={renderCustomLabel}
                        labelLine={false}
                      >
                        {distributionData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [value, 'العدد']}
                        contentStyle={{ direction: 'rtl', textAlign: 'right' }}
                        labelStyle={{ direction: 'rtl' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="dashboard-chart-card">
                <h3 className="chart-card-title">مقارنة الأعداد</h3>
                <p className="chart-card-desc">إجمالي العناصر مقابل العناصر النشطة</p>
                <div className="chart-container chart-container--bar">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={barData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis type="number" allowDecimals={false} style={{ fontSize: '12px' }} />
                      <YAxis type="category" dataKey="name" width={80} style={{ fontSize: '12px' }} tick={{ fill: '#333' }} />
                      <Tooltip
                        contentStyle={{ direction: 'rtl', textAlign: 'right' }}
                        formatter={(value) => [value, '']}
                        labelFormatter={(label) => label}
                      />
                      <Legend wrapperStyle={{ direction: 'rtl' }} />
                      <Bar dataKey="total" fill={CHART_COLORS[0]} name="إجمالي" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="active" fill="#2d7d46" name="نشط" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="dashboard-chart-card dashboard-chart-card--full">
              <h3 className="chart-card-title">النشط مقابل غير النشط</h3>
              <p className="chart-card-desc">توزيع العناصر النشطة والمعطلة حسب الفئة</p>
              <div className="chart-container chart-container--stacked">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={activeVsInactive} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="name" style={{ fontSize: '12px' }} tick={{ fill: '#333' }} />
                    <YAxis allowDecimals={false} style={{ fontSize: '12px' }} />
                    <Tooltip
                      contentStyle={{ direction: 'rtl', textAlign: 'right' }}
                      formatter={(value) => [value, '']}
                    />
                    <Legend wrapperStyle={{ direction: 'rtl' }} />
                    <Bar dataKey="active" stackId="a" fill="#2d7d46" name="نشط" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="inactive" stackId="a" fill="#999" name="غير نشط" radius={[0, 0, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="dashboard-summary">
            <div className="summary-item">
              <span className="summary-label">إجمالي العناصر في المتجر:</span>
              <span className="summary-value">{totalItems}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">إجمالي العناصر النشطة:</span>
              <span className="summary-value summary-value--green">
                {activeModels + activeVariants + activeSpare + activeAccessories}
              </span>
            </div>
          </div>
        </>
      )}

      {totalItems === 0 && (
        <div className="dashboard-empty-charts">
          <p>لا توجد بيانات كافية لعرض الرسوم البيانية. أضف موديلات وأنواع وأجزاء وقطع غيار.</p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
